/**
 * Sesión del portal de cliente.
 *
 * Separada de la del personal, y no sólo en el código: el token que emite
 * el servidor lleva `aud: 'PORTAL'` y la API de gestión lo rechaza. Aquí
 * lo importante es que **el portal nunca pide un identificador de
 * usuario**: lo que se ve sale de la sesión.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiClient } from '../session/api-client.js';

export interface PortalUser {
  id: string;
  customerId: string;
  email: string;
  fullName: string;
  role: string;
  siteIds: string[];
}

interface PortalSessionState {
  user: PortalUser | null | undefined;
  api: ApiClient;
  login: (email: string, password: string, companyTaxId?: string) => Promise<void>;
  logout: () => void;
}

const PortalSessionContext = createContext<PortalSessionState | null>(null);

export function PortalSessionProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUser] = useState<PortalUser | null | undefined>(undefined);
  const api = useMemo(() => new ApiClient({ onSessionLost: () => setUser(null) }), []);

  useEffect(() => {
    let cancelled = false;

    api
      .get<PortalUser>('/portal/me')
      .then((me) => {
        if (!cancelled) setUser(me);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  const login = useCallback(
    async (email: string, password: string, companyTaxId?: string) => {
      await api.post('/portal/session', {
        email,
        password,
        ...(companyTaxId ? { companyTaxId } : {}),
      });
      setUser(await api.get<PortalUser>('/portal/me'));
    },
    [api],
  );

  const logout = useCallback(() => {
    // El portal no tiene endpoint de cierre propio: se olvida la sesión
    // local y las cookies caducan. Un cliente que cierra sesión en un
    // ordenador compartido espera que no quede nada a la vista.
    setUser(null);
  }, []);

  const value = useMemo<PortalSessionState>(
    () => ({ user, api, login, logout }),
    [user, api, login, logout],
  );

  return <PortalSessionContext.Provider value={value}>{children}</PortalSessionContext.Provider>;
}

export function usePortalSession(): PortalSessionState {
  const context = useContext(PortalSessionContext);
  if (!context) throw new Error('usePortalSession fuera de <PortalSessionProvider>');
  return context;
}
